import { Button, buttonVariants } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import type { VariantProps } from "class-variance-authority"
import { Info, LoaderCircle } from "lucide-react"
import { createContext, useContext, useImperativeHandle, useRef, useState } from "react"

type HUConfirmPopupProps = {
  accept: () => Promise<void> | void
  cancel?: () => Promise<void> | void
  handleErrorMessage?: (e: any) => string
  acceptValidated?: string
  title?: string | React.ReactNode
  message?: string | React.ReactNode
  footerProps?: {
    confirmText?: string
    cancelText?: string
  }
  classNames?: {
    dialogContent?: string
    dialogHeader?: string
    dialogTitle?: string
    dialogMessage?: string
    dialogFooter?: string
    dialogFooterConfirm?: string
    dialogFooterCancel?: string
    dialogFooterConfirmClassName?: string
  }
  dialogProps?: React.ComponentProps<typeof Dialog>
  dialogContentProps?: React.ComponentProps<typeof DialogContent>
  variant?: {
    dialogFooterConfirm?: VariantProps<typeof buttonVariants>["variant"]
    dialogFooterCancel?: VariantProps<typeof buttonVariants>["variant"]
  }
}

type HUConfirmPopupContextAction = {
  show: (props: HUConfirmPopupProps) => void
}

export const HUConfirmPopupContext = createContext<HUConfirmPopupContextAction>({
  show: () => {},
})

function InternalConfirmPopup({ ref }: { ref: React.Ref<HUConfirmPopupContextAction> }) {
  const [popupProps, setPopupProps] = useState<HUConfirmPopupProps | null>(null)
  const [visible, setVisible] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)

  useImperativeHandle(ref, () => {
    return {
      show: (props: HUConfirmPopupProps) => {
        setPopupProps(props)
        setVisible(true)
      },
    }
  }, [])
  const confirmText = popupProps?.footerProps?.confirmText ?? word("global.yes")
  const cancelText = popupProps?.footerProps?.cancelText ?? word("global.cancel")

  const onOpenChange: NonNullable<React.ComponentProps<typeof Dialog>["onOpenChange"]> = (
    open,
    eventDetails,
  ) => {
    if (popupProps?.dialogProps?.onOpenChange) {
      popupProps.dialogProps.onOpenChange(open, eventDetails)
    } else {
      void popupProps?.cancel?.()
      setVisible(false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={onOpenChange} {...popupProps?.dialogProps}>
      <DialogContent
        className={cn("flex w-90 flex-col", popupProps?.classNames?.dialogContent)}
        aria-describedby={undefined}
        {...popupProps?.dialogContentProps}
      >
        <DialogHeader className={popupProps?.classNames?.dialogHeader}>
          <DialogTitle className={popupProps?.classNames?.dialogTitle}>
            <Text fontStyle="TL" className="w-full wrap-break-word">
              {popupProps?.title ?? word("global.confirm.title")}
            </Text>
          </DialogTitle>
        </DialogHeader>
        {typeof popupProps?.message === "string" || !popupProps?.message ? (
          <div className={cn("flex w-full items-center justify-center", popupProps?.classNames?.dialogMessage)}>
            <Info color="currentColor" size={24} className="mr-2 shrink-0" />
            <Text fontStyle="BM" className="w-full wrap-break-word">
              {popupProps?.message ?? word("global.confirm.message")}
            </Text>
          </div>
        ) : (
          popupProps.message
        )}
        <DialogFooter className={popupProps?.classNames?.dialogFooter}>
          <DialogClose
            render={
              <Button
                variant={popupProps?.variant?.dialogFooterCancel ?? "outline"}
                onClick={() => {
                  setVisible(false)
                }}
                className={popupProps?.classNames?.dialogFooterCancel}
                data-testid="confirm-popup-no"
              >
                {cancelText}
              </Button>
            }
          />
          <Button
            variant={popupProps?.variant?.dialogFooterConfirm ?? "primary"}
            onClick={() => {
              setLoading(true)
              try {
                void popupProps?.accept?.()
              } catch (e: any) {
                // TODO : sentry error gestion d'erreur sur Sentry
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument , @typescript-eslint/no-unsafe-member-access
                toast.error(popupProps?.handleErrorMessage?.(e) ?? e.messag, {
                  classNames: errorToastClassName(),
                })
              } finally {
                setLoading(false)
                setVisible(false)
              }
            }}
            className={popupProps?.classNames?.dialogFooterConfirm}
            data-testid="confirm-popup-yes"
            autoFocus
            isLoading={loading}
          >
            {loading && <LoaderCircle className="animate-spin" color="currentColor" size={13} />}
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function HUConfirmPopup({ children }: { children: React.ReactNode }) {
  const internalRef = useRef<HUConfirmPopupContextAction>(null)

  return (
    <HUConfirmPopupContext.Provider value={{ show: (props) => internalRef.current?.show(props) }}>
      <InternalConfirmPopup ref={internalRef} />
      {children}
    </HUConfirmPopupContext.Provider>
  )
}

export const useConfirmPopup = () => useContext(HUConfirmPopupContext)
